import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'src/store';
import { basicOverlay, get_assets, load_asset, delete_assets, delete_selected_assets, rename_asset } from 'src/services/api';

export interface BasicState {
    value: number;
    pointData: any;
    map: any;
    userMessage: string;
    status: 'idle' | 'loading' | 'failed';
}

const initialState: BasicState = {
    value: 0,
    pointData: { coords: [], ids: [], site_name: [], values: [] },
    map: { variable: '', scenario: '', year: '', opacity: 0.5 },
    userMessage: "",
    status: 'idle',
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const overlayAsync = createAsyncThunk(
    // https://blog.logrocket.com/using-redux-toolkits-createasyncthunk/
    // v-- will become api/overlay/{pending,fulfilled,rejected}
    'api/overlay',
    async (latlng: any) => {
        const response = await basicOverlay(latlng);
        // The value we return becomes the `fulfilled` action payload
        console.log("Api output 2: " + response);
        return response;
    }
);

// does this need to be async??
export const addPoint = createAsyncThunk(
    'api/addPoint',
    async (latlng: any) => {
        // convert to simple dictionary. o.w., get non-serializable object error
        // since latlng is not a simple dict (has .toBounds and stuff)
        // https://stackoverflow.com/questions/61704805/getting-an-error-a-non-serializable-value-was-detected-in-the-state-when-using
        //const response = await basicOverlay(latlng);
        return { lat: latlng.lat, lng: latlng.lng };
    }
);

export const loadAsset = createAsyncThunk(
    'api/load_asset', // not sure what this argument does lol
    async (latlng: any) => {
        const response = await load_asset(latlng);
        // TODO - consider whether it's cleaner to just have the API return the coords (along with the risk vars)
        return { lat: latlng.lat, lng: latlng.lng, 'response': response };
    }
);

export const getAssets = createAsyncThunk(
    'api/get_assets',
    async () => {
        const response = await get_assets();
        return { coords: response.coords, ids: response.id, site_name: response.site_name, risk: response.risk };
    }
);

export const deleteAssets = createAsyncThunk(
    'api/delete_assets',
    async () => {
        await delete_assets();
        return null;
    }
);

export const deleteSelectedAssets = createAsyncThunk(
    'api/delete_selected_assets',
    async (id_list: any) => {
        await delete_selected_assets(id_list);
        return { id_list: id_list };
    }
);

// https://stackoverflow.com/questions/64742747/how-do-you-pass-arguments-to-createasyncthunk-in-redux-toolkit
export const renameAsset = createAsyncThunk(
    'api/rename_asset',
    async (rename_args: any) => {
        await rename_asset(rename_args.id, rename_args.new_name);
        return { id: rename_args.id, new_name: rename_args.new_name };
    }
);

export const basicSlice = createSlice({
    name: 'basic',
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        // mutation okay with createSlice https://redux.js.org/tutorials/essentials/part-2-app-structure
        setUserMessage: (state, action) => {
            state.userMessage = action.payload;
        },
        setMap: (state, action) => {
            state.map[action.payload.name] = action.payload.value;
        },
        resetStore: (state) => {
            console.log("resetting store");
            // evidently stuff like
            // state = initialState;
            // state = structuredClone(initialState);
            // won't work. But, we can just return initialState to reset.
            // https://github.com/reduxjs/redux/issues/432
            // https://redux.js.org/tutorials/fundamentals/part-8-modern-redux#immutable-updates-with-immer
            // returning state: https://github.com/immerjs/immer/issues/296
            return initialState;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(overlayAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(overlayAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                console.log("Api output 3: " + action);
                state.value += action.payload;
                state.pointData.values.push(action.payload);
            })
            .addCase(overlayAsync.rejected, (state) => {
                state.status = 'failed';
            })

            .addCase(addPoint.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(addPoint.fulfilled, (state, action) => {
                state.status = 'idle';
                state.pointData.coords.push(action.payload);
                console.log("added point");
                console.log(state.pointData.coords);
                console.log(action.payload);
            })
            .addCase(addPoint.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(loadAsset.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadAsset.fulfilled, (state, action) => {
                state.status = 'idle';
                if (action.payload.response != "") { // will be "" if over ocean, asset limit reached, or already exists
                    state.pointData.coords.push({ 'lat': action.payload.lat, 'lng': action.payload.lng });
                    state.pointData.ids.push(action.payload.response.id);
                    state.pointData.site_name.push(action.payload.response.site_name);
                    state.pointData.values.push(action.payload.response.risk);
                }
                console.log("UPDATE FFS");
                console.log(action.payload);
            })
            .addCase(loadAsset.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(getAssets.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getAssets.fulfilled, (state, action) => {
                state.status = 'idle';
                console.log("getting assets");
                state.pointData.coords = action.payload.coords;
                state.pointData.ids = action.payload.ids;
                state.pointData.site_name = action.payload.site_name;
                state.pointData.values = action.payload.risk;
                console.log(action.payload);
            })
            .addCase(getAssets.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(deleteAssets.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteAssets.fulfilled, (state, action) => {
                state.status = 'idle';
                console.log("deleting assets");
                state.pointData.coords = [];
                state.pointData.ids = [];
                state.pointData.site_name = [];
                state.pointData.values = [];
            })
            .addCase(deleteAssets.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(deleteSelectedAssets.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteSelectedAssets.fulfilled, (state, action) => {
                state.status = 'idle';
                // to get new state, we can (1) call getAssets, (2) manually delete
                // assets in javascript, or (3) have deleteSelectedAssets return
                // new list of assets. Going w/ #2 for now.
                let id_list = action.payload.id_list
                console.log("deleting selected assets");
                console.log(id_list);
                for (let i = state.pointData.coords.length - 1; i >= 0; i--) {
                    if (id_list.includes(state.pointData.ids[i])) {
                        state.pointData.coords.splice(i, 1);
                        state.pointData.ids.splice(i, 1);
                        state.pointData.site_name.splice(i, 1);
                        state.pointData.values.splice(i, 1);
                    }
                }
            })
            .addCase(deleteSelectedAssets.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(renameAsset.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(renameAsset.fulfilled, (state, action) => {
                console.log("XXX");
                console.log(action.payload);
                state.status = 'idle';
                for (let i = state.pointData.coords.length - 1; i >= 0; i--) {
                    if (action.payload.id == state.pointData.ids[i]) {
                        state.pointData.site_name[i] = action.payload.new_name;
                    }
                }
            })
            .addCase(renameAsset.rejected, (state) => {
                state.status = 'failed';
            });
    },
});

// The function below is called a selector and allows us to select a value from
// the state.
export const selectBasic = (state: RootState) => state.basic.value;

export const selectValues = (state: RootState) => state.basic.pointData.values;

export const select_userMessage = (state: RootState) => state.basic.userMessage;

export default basicSlice.reducer;

export const { setUserMessage, setMap, resetStore } = basicSlice.actions
